import clsx from "clsx";
import React from "react";
import { MenuProps, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MoreHoriz } from "@mui/icons-material";
import { ConfirmActionDialog } from "../../dialog/ConfirmActionDialog";
import {
  ExerciseTagDto,
  TagDto,
} from "@growth-machine-llc/stridist-api-client";
import { TagType } from "../../../constants";
import useDeleteTag from "./hooks/useDeleteTag";
import { Pencil, Trash2 } from "lucide-react";
import { ListMenu } from "../../menu/ListMenu";

const useStyles = makeStyles(() => ({
  root: {},
}));

export interface TagMenuProps extends Omit<MenuProps, "open" | "onClose"> {
  tag: ExerciseTagDto | TagDto;
  onEdit?: () => void;
  tagType: TagType;
}

export function TagMenu(props: TagMenuProps) {
  const { className, tag, onEdit, tagType, ...other } = props;
  const { mutate: deleteTag } = useDeleteTag({ tagType });

  const s = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [confirmTagDeleteDialogOpen, setConfirmTagDeleteDialogOpen] =
    React.useState(false);

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleTagDelete = React.useCallback(() => {
    setAnchorEl(null);
    setConfirmTagDeleteDialogOpen(true);
  }, [tag, setConfirmTagDeleteDialogOpen]);

  const handleTagUpdate = React.useCallback(() => {
    setAnchorEl(null);
    if (onEdit) {
      onEdit();
    }
  }, [tag, setConfirmTagDeleteDialogOpen, onEdit]);

  const handleConfirmTagDelete = React.useCallback(() => {
    deleteTag(tag.id);
    setConfirmTagDeleteDialogOpen(false);
  }, [tag, deleteTag]);

  const handleCancelExerciseDelete = React.useCallback(() => {
    setConfirmTagDeleteDialogOpen(false);
  }, []);

  const menuOptions = [
    {
      label: "Update",
      onClick: handleTagUpdate,
      icon: <Pencil />,
    },
    {
      label: "Delete",
      onClick: handleTagDelete,
      icon: <Trash2 />,
    },
  ];

  return (
    <>
      <IconButton onClick={handleOpen} size="large">
        <MoreHoriz />
      </IconButton>
      <ListMenu
        id={tag.id.toString()}
        options={menuOptions}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        keepMounted
        onClose={handleClose}
        className={clsx(s.root, className)}
        {...other}
      />

      {/* TODO_EXERCISE_LIBRARY: Add informative description*/}
      <ConfirmActionDialog
        title={`Are you sure you want to delete ${tagType.toLocaleLowerCase()} tag?`}
        open={confirmTagDeleteDialogOpen}
        onClose={handleCancelExerciseDelete}
        onCancel={handleCancelExerciseDelete}
        onConfirm={handleConfirmTagDelete}
      />
    </>
  );
}
